import debug from 'debug';
import { isEmpty, isEqual, pick } from '@pie-element/shared-lodash';
import type { ExtraCSSRules, PieEnvironment } from '@pie-element/shared-types';
import defaults from './defaults.js';

import { partialScoring } from '@pie-element/shared-controller-utils';
import type {
  ChartingCategory,
  ChartingCategoryCorrectness,
  ChartingConfiguration,
  ChartingCorrectAnswer,
  ChartingModel,
  ChartingSession,
} from '../types.js';

/** The environment, plus the extra properties a host may ask the outcome for */
export type ChartingEnvironment = PieEnvironment & { extraProps?: { correctResponseEnabled?: boolean } };

/** A category marked correct or incorrect */
export type ScoredCategory = ChartingCategory & { correctness: ChartingCategoryCorrectness };

/** A category whose value and label both match the correct answer */
export type ChartingCorrectResponse = { label: string; index?: number };

export type ChartingScore = {
  score: number;
  answers: ScoredCategory[];
  correctResponses?: ChartingCorrectResponse[];
};

export type ChartingValidationErrors = {
  teacherInstructions?: string;
  prompt?: string;
  rationale?: string;
  /** Category errors of the correct answer, keyed by category position */
  categoryErrors?: Record<number, string>;
  correctAnswerErrors?: {
    categoriesError?: string;
    identicalError?: string;
  };
};

/** What `model()` returns for the delivery view */
export type ChartingViewModel = {
  addCategoryEnabled?: boolean;
  chartType?: ChartingModel['chartType'];
  data: Omit<ChartingCategory, 'deletable'>[];
  domain?: ChartingModel['domain'];
  graph?: ChartingModel['graph'];
  prompt?: string | null;
  range?: ChartingModel['range'];
  rationale?: string | null;
  title?: string;
  size?: ChartingModel['graph'];
  showToggle: boolean;
  correctness: { correctness: string; score: string };
  disabled: boolean;
  scoringType?: ChartingModel['scoringType'];
  studentNewCategoryDefaultLabel?: string;
  language?: string;
  env: ChartingEnvironment;
  extraCSSRules?: ExtraCSSRules;
  correctedAnswer?: Omit<ChartingCategory, 'deletable'>[];
  correctAnswer?: ChartingCorrectAnswer;
  showKeyLegend?: boolean;
  teacherInstructions?: string | null;
};

type ChartingOutcome = {
  score: number;
  empty: boolean;
  traceLog: string[];
  extraProps?: { correctResponse?: ChartingCorrectResponse[] };
};

const log = debug('@pie-element:graphing:controller');

const lowerCase = (string?: string) => (string || '').toLowerCase();

export const checkLabelsEquality = (givenAnswerLabel?: string, correctAnswerLabel?: string) =>
  lowerCase(givenAnswerLabel) === lowerCase(correctAnswerLabel);

export const setCorrectness = (answers: ChartingCategory[] | undefined, partialScoring: boolean): ScoredCategory[] =>
  answers
    ? answers.map((answer) => ({
        ...answer,
        correctness: {
          value: partialScoring ? 'incorrect' : 'correct',
          label: partialScoring ? 'incorrect' : 'correct',
        },
      }))
    : [];

export const normalize = (question: ChartingModel) => ({ ...defaults, ...question });

export const getScore = (
  question: ChartingModel,
  session: ChartingSession | null | undefined,
  env: Partial<ChartingEnvironment> = {},
): ChartingScore => {
  const { correctAnswer, data: initialData = [], scoringType } = question;
  const correctResponses: ChartingCorrectResponse[] = [];

  const isPartialScoring = partialScoring.enabled(
    { partialScoring: scoringType !== undefined ? scoringType === 'partial scoring' : scoringType },
    env,
  );

  const { data: correctAnswers = [] } = correctAnswer || {};
  const defaultAnswers = filterCategories(initialData);

  let answers = setCorrectness((session && session.answer) || defaultAnswers, isPartialScoring);

  let result = 0;

  if (isPartialScoring) {
    // if score type is "partial scoring"
    // maxScore is calculated based on the correct response
    // score is calculated based on the given response
    let maxScore = 0;
    let score = 0;

    const scoreForLabelAndValueEditable = (answer: ScoredCategory, corrAnswer: ChartingCategory) => {
      const { value, label, index } = answer;
      const valueIsCorrect = value === corrAnswer.value;
      const labelIsCorrect = checkLabelsEquality(label, corrAnswer.label);
      maxScore += 2;

      if (valueIsCorrect) {
        score += 1;
        answer.correctness.value = 'correct';
      }

      if (labelIsCorrect) {
        score += 1;
        answer.correctness.label = 'correct';
      }

      if (valueIsCorrect && labelIsCorrect) {
        correctResponses.push({ label: label, index: index });
      }
    };

    // if given answer has more categories than the correct answers, the "extra" will be ignored
    correctAnswers.forEach((corrAnswer, index) => {
      const defaultAnswer = defaultAnswers[index];
      const answer = answers[index];

      // if there is a corresponding category at the same position in the given answer
      if (answer) {
        // if there is a corresponding category at the same position in the default answer
        if (defaultAnswer) {
          // if category's label (in default answer) was not editable
          // it means that this category values only one point (only the value can be changed)
          if (!defaultAnswer.editable && answer.interactive) {
            maxScore += 1;

            if (answer.value === corrAnswer.value) {
              score += 1;
              answer.correctness.value = 'correct';
              correctResponses.push({ label: answer.label, index: index });
            }
            answer.correctness.label = 'correct';

            // if category's label (in default answer) was editable
            // it means that this category values 2 points (both label and value can be changed)
          } else if (defaultAnswer.editable && answer.interactive) {
            scoreForLabelAndValueEditable(answer, corrAnswer);
          } else if (!answer.interactive) {
            answer.correctness.value = 'correct';
            answer.correctness.label = 'correct';
            correctResponses.push({ label: answer.label, index: index });
          }
        } else {
          // if there is not a corresponding category at the same position in the default answer
          scoreForLabelAndValueEditable(answer, corrAnswer);
        }
      } else {
        // if there is not a corresponding category at the same position in the given answer
        // it means that the given answer has less categories than the correct answer
        maxScore += 2;
      }
    });

    result = maxScore ? score / maxScore : 0;
  } else {
    // all-or-nothing scoring: overall score is 1 only if lengths and all values/labels match
    result = correctAnswers.length === answers.length ? 1 : 0;

    // regardless of overall result, mark each answer individually for user feedback
    answers = answers.map((answer, index): ScoredCategory => {
      const correctAnswer = correctAnswers[index];
      const valueIsCorrect = correctAnswer ? answer.value === correctAnswer.value : false;
      const labelIsCorrect = correctAnswer ? lowerCase(answer.label) === lowerCase(correctAnswer.label) : false;

      if (!valueIsCorrect || !labelIsCorrect) {
        result = 0;
      }

      if (valueIsCorrect && labelIsCorrect) {
        correctResponses.push({ label: answer.label, index });
      }

      return {
        ...answer,
        correctness: {
          value: valueIsCorrect ? 'correct' : 'incorrect',
          label: labelIsCorrect ? 'correct' : 'incorrect',
        },
      };
    });
  }

  const score: ChartingScore = {
    score: parseFloat(result.toFixed(2)),
    answers,
  };

  if (env.extraProps && env.extraProps.correctResponseEnabled) {
    score.correctResponses = correctResponses;
  }

  return score;
};

/**
 * Generates detailed trace log for scoring evaluation
 * @param {Object} model - the question model
 * @param {Object} session - the student session
 * @param {Object} env - the environment
 * @returns {Array} traceLog - array of trace messages
 */
export const getLogTrace = (
  model: ChartingModel,
  session: ChartingSession | null | undefined,
  env: ChartingEnvironment,
): string[] => {
  const traceLog: string[] = [];

  const { correctAnswer, data: initialData = [], scoringType } = model || {};
  const { data: correctAnswers = [] } = correctAnswer || {};
  const studentAnswers = (session && session.answer) || [];

  const isPartialScoring = partialScoring.enabled(
    { partialScoring: scoringType !== undefined ? scoringType === 'partial scoring' : scoringType },
    env
  );

  const defaultAnswers = filterCategories(initialData);
  const answers = studentAnswers.length > 0 ? filterCategories(studentAnswers) : defaultAnswers;

  if (!studentAnswers.length) {
    traceLog.push(`Student did not modify the chart. Initial chart is scored.`);
  }

  correctAnswers.forEach((correct, index) => {
    const initial = defaultAnswers[index];
    const student = answers[index];

    if (!student) {
      traceLog.push(`Category ${index + 1}: missing (expected value '${correct.value}', label '${correct.label}').`);
      return;
    }

    const valueCorrect = student.value === correct.value;
    const labelCorrect = checkLabelsEquality(student.label, correct.label);

    if (!student.interactive) {
      traceLog.push(`Category ${index + 1}: non-interactive category treated as correct.`);
      return;
    }

    if (studentAnswers.length > 0) {
      const valueChanged = initial ? student.value !== initial.value : true;
      const labelChanged = initial ? !checkLabelsEquality(student.label, initial.label) : true;

      if (!valueChanged && !labelChanged) {
        if (valueCorrect && labelCorrect) {
          traceLog.push(`Category ${index + 1}: unchanged from initial value and correct.`);
        } else {
          traceLog.push(`Category ${index + 1}: unchanged from initial value but incorrect.`);
        }
        return;
      }

      const parts: string[] = [];
      if (valueChanged) {
        parts.push(`value ${valueCorrect ? 'correct' : 'incorrect'}`);
      }
      if (labelChanged) {
        parts.push(`label ${labelCorrect ? 'correct' : 'incorrect'}`);
      }
      traceLog.push(`Category ${index + 1}: modified - ${parts.join(', ')}.`);
    } else {
      if (valueCorrect && labelCorrect) {
        traceLog.push(`Category ${index + 1}: initial values are correct.`);
      } else if (valueCorrect) {
        traceLog.push(`Category ${index + 1}: initial value correct, label incorrect.`);
      } else if (labelCorrect) {
        traceLog.push(`Category ${index + 1}: initial value incorrect, label correct.`);
      } else {
        traceLog.push(`Category ${index + 1}: initial value and label incorrect.`);
      }
    }
  });

  if (answers.length > correctAnswers.length) {
    const extra = answers.length - correctAnswers.length;
    traceLog.push(`${extra} extra category(ies) provided beyond expected number.`);
  }

  if (isPartialScoring) {
    traceLog.push('Score calculated using partial scoring.');
    traceLog.push('Student receives credit for each correct value and label independently.');
  } else {
    traceLog.push('Score calculated using all-or-nothing scoring.');
    traceLog.push('Student must match all category values and labels exactly for full credit.');
  }

  const scoreObject = getScore(model, session, env);
  traceLog.push(`Score: ${scoreObject.score}.`);

  return traceLog;
};


// eslint-disable-next-line no-unused-vars
export const filterCategories = <T extends ChartingCategory>(categories?: T[]): Omit<T, 'deletable'>[] =>
  categories ? categories.map(({ deletable, ...rest }) => rest) : [];

export function model(
  question: ChartingModel,
  session: ChartingSession | null | undefined,
  env: ChartingEnvironment,
): Promise<ChartingViewModel> {
  return new Promise((resolve) => {
    const normalizedQuestion = normalize(question);
    const {
      addCategoryEnabled,
      chartType,
      data,
      domain,
      graph,
      prompt,
      promptEnabled,
      range,
      rationale,
      title,
      rationaleEnabled,
      teacherInstructions,
      teacherInstructionsEnabled,
      correctAnswer,
      scoringType,
      studentNewCategoryDefaultLabel,
      language,
      extraCSSRules,
    } = normalizedQuestion;

    const correctInfo = { correctness: 'incorrect', score: '0%' };

    const base: ChartingViewModel = {
      addCategoryEnabled,
      chartType,
      data: filterCategories(data),
      domain,
      graph,
      prompt: promptEnabled ? prompt : null,
      range,
      rationale,
      title,
      size: graph,
      showToggle: false,
      correctness: correctInfo,
      disabled: env.mode !== 'gather',
      scoringType,
      studentNewCategoryDefaultLabel,
      language,
      env,
      extraCSSRules,
    };

    const scoreObject = getScore(normalizedQuestion, session, env);
    const answers = filterCategories(scoreObject.answers);

    if (env.mode === 'view') {
      // eslint-disable-next-line no-unused-vars
      base.correctedAnswer = answers.map(({ correctness, ...rest }) => {
        return { ...rest, interactive: false };
      });

      base.addCategoryEnabled = false;
    }

    if (env.mode === 'evaluate') {
      base.correctedAnswer = answers;
      base.correctAnswer = correctAnswer;
      base.showToggle = !!correctAnswer?.data?.length && scoreObject.score !== 1;
      base.addCategoryEnabled = false;
      base.showKeyLegend = true;
    }

    if (env.role === 'instructor' && (env.mode === 'view' || env.mode === 'evaluate')) {
      base.rationale = rationaleEnabled ? rationale : null;
      base.teacherInstructions = teacherInstructionsEnabled ? teacherInstructions : null;
    } else {
      base.rationale = null;
      base.teacherInstructions = null;
    }

    log('base: ', base);
    resolve(base);
  });
}

export function outcome(
  model: ChartingModel,
  session: ChartingSession | null | undefined,
  env: ChartingEnvironment,
): Promise<ChartingOutcome> {
  return new Promise((resolve) => {
    const traceLog = getLogTrace(model, session, env);
    const scoreObject = getScore(model, session, env);
    
    const result: ChartingOutcome = {
      score: scoreObject.score,
      empty: !session || isEmpty(session),
      traceLog
    };
    
    if (env.extraProps && env.extraProps.correctResponseEnabled) {
      result.extraProps = { correctResponse: scoreObject.correctResponses };
    }
    
    resolve(result);
  });
}

export const createCorrectResponseSession = (
  question: ChartingModel,
  env: ChartingEnvironment,
): Promise<ChartingSession | null> => {
  return new Promise((resolve) => {
    if (env.mode !== 'evaluate' && env.role === 'instructor') {
      const { correctAnswer } = question;

      let answers = correctAnswer && correctAnswer.data;

      // for IBX preview mode
      if (env.mode === 'gather') {
        const { data } = question;

        answers = ((correctAnswer && correctAnswer.data) || []).map((answer, index) => {
          // Throws when the model has no `data`, as it did untyped.
          return {
            ...(data as ChartingCategory[])[index],
            ...answer,
          };
        });
      }

      resolve({
        answer: answers,
        id: '1',
      });
    } else {
      return resolve(null);
    }
  });
};

// remove all html tags
const getInnerText = (html?: string) => (html || '').replaceAll(/<[^>]*>/g, '');

// remove all html tags except img, iframe and source tag for audio
const getContent = (html?: string) => (html || '').replace(/(<(?!img|iframe|source)([^>]+)>)/gi, '');

export const validate = (
  model: Partial<ChartingModel> = {},
  config: ChartingConfiguration = {},
): ChartingValidationErrors => {
  const { correctAnswer, data } = model || {};
  const { data: correctData } = correctAnswer || {};
  const categories = correctData || [];

  const errors: ChartingValidationErrors = {};
  const correctAnswerErrors: NonNullable<ChartingValidationErrors['correctAnswerErrors']> = {};
  const categoryErrors: Record<number, string> = {};

  (['teacherInstructions', 'prompt', 'rationale'] as const).forEach((field) => {
    if (config[field]?.required && !getContent(model[field])) {
      errors[field] = 'This field is required.';
    }
  });

  categories.forEach((category, index) => {
    const { label } = category;

    if (!getInnerText(label)) {
      categoryErrors[index] = 'Content should not be empty. ';
    } else {
      const identicalAnswer = categories.some((c, i) => c.label === label && index !== i);

      if (identicalAnswer) {
        categoryErrors[index] = 'Category names should be unique. ';
      }
    }
  });

  if (categories.length < 1 || categories.length > 20) {
    correctAnswerErrors.categoriesError = 'The correct answer should include between 1 and 20 categories.';
  } else if (
    isEqual(
      // `data.map` throws when the model has no `data`, as it did untyped.
      (data as ChartingCategory[]).map((category) => pick(category, 'value', 'label')),
      (correctData as ChartingCategory[]).map((category) => pick(category, 'value', 'label')),
    )
  ) {
    correctAnswerErrors.identicalError = 'Correct answer should not be identical to the chart’s initial state';
  }

  if (!isEmpty(categoryErrors)) {
    errors.categoryErrors = categoryErrors;
  }

  if (!isEmpty(correctAnswerErrors)) {
    errors.correctAnswerErrors = correctAnswerErrors;
  }

  return errors;
};
